import { captureConsoleIntegration } from "@sentry/core";
import * as Sentry from "@sentry/vue";
import { browserTracingIntegration } from "@sentry/vue";
import mitt from "mitt";
import { createPinia } from "pinia";
import { createApp } from "vue";
import { watch } from "vue";

import { initializeAxios } from "@/axiosClient";
import { useAuthStore } from "@/stores/auth";

import App from "./App.vue";
import clickOutside from "./directives/click.outside";
import infocus from "./directives/infocus";
import router from "./router";
import { piniaLocalStoragePlugin } from "./stores";
import { useMiTiendaStore } from "./stores/mitienda";
import { useReemplazosStore } from "./stores/reemplazos";
import { useUserAccessesStore } from "./stores/user-access";

const app = createApp(App);

// Pinia
const pinia = createPinia();
pinia.use(piniaLocalStoragePlugin);
app.use(pinia);

const authStore = useAuthStore();
app.config.globalProperties.$usuario = authStore.user;

watch(
  () => authStore.user,
  (newUser) => {
    app.config.globalProperties.$usuario = newUser;
  },
  { deep: true },
);

app.use(router);

// Variables globales
const app_mode = import.meta.env.VITE_MODE;
app.config.globalProperties.$app_mode = app_mode;
app.config.globalProperties.$localurl = import.meta.env.VITE_URL;
app.config.globalProperties.$cyo_url = import.meta.env.VITE_CYO_URL;
app.config.globalProperties.$cc_url = import.meta.env.VITE_CC_URL;
app.config.globalProperties.$pog_url = import.meta.env.VITE_POG_URL;
app.config.globalProperties.$mialta_url = import.meta.env.VITE_MIALTA_URL;
app.config.globalProperties.$mispedidos_url =
  import.meta.env.VITE_MISPEDIDOS_URL;
app.config.globalProperties.$misdocumentos_url =
  import.meta.env.VITE_MISDOCUMENTOS_URL;
app.config.globalProperties.$simulador_url = import.meta.env.VITE_SIMULADOR_URL;
app.config.globalProperties.$dev = app_mode !== "prod";
app.config.globalProperties.$usuario = {
  rol: {},
};

export const eventBus = mitt();
app.config.globalProperties.$eventBus = eventBus;

// Axios
const axios = initializeAxios(app.config.globalProperties.$localurl);
app.config.globalProperties.$axios = axios;

// Directivas globales
app.directive("click-outside", clickOutside);
app.directive("infocus", infocus);

// Sentry
if (app_mode === "prod") {
  Sentry.init({
    app,
    dsn: "https://ab9208444de6fad48da1046a0eea4598@o4510261876162560.ingest.us.sentry.io/4510261881208832",
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    environment: app_mode === "prod" ? "production" : "development",
    sendDefaultPii: true,
    integrations: [
      captureConsoleIntegration({
        levels: ["error"],
      }),
      browserTracingIntegration({
        router,
        tracingOrigins: [
          "micuenta.agro.basf.com",
          "dev.micuenta.basf.com.ar",
          /^\//,
        ],
      }),
    ],
    attachProps: true,
    logErrors: true,
    attachStacktrace: true,
    tracesSampleRate: 1.0,
  });
}

// Montar la app
app.mount("#app");

// Event listeners
window.addEventListener("unauthorized", () => {
  if (
    router.currentRoute.value.meta.requiresAuth ||
    (!router.currentRoute.value.meta.title && !router.currentRoute.value.name)
  ) {
    // Token expirado o inválido
    clearUserDataAndRedirect();
  }
});
window.addEventListener("logout", clearUserDataAndRedirect);

let isRedirecting = false;
function clearUserDataAndRedirect() {
  if (isRedirecting) {
    return;
  }
  isRedirecting = true;

  const reemplazosStore = useReemplazosStore();
  const userAccessesStore = useUserAccessesStore();
  const miTiendaStore = useMiTiendaStore();

  // Realizar una solicitud al backend para eliminar la cookie
  axios.post("/api/auth/logout").finally(() => {
    // Continuar con el borrado local y redirección
    localStorage.removeItem("token");
    localStorage.removeItem("originalToken");
    localStorage.removeItem("token-landing");

    reemplazosStore.resetState();
    authStore.clearUser();
    userAccessesStore.resetState();
    miTiendaStore.resetState();

    if (app_mode === "prod") {
      Sentry.getCurrentScope().setUser({});
    }

    localStorage.removeItem("reemplazo-id");
    localStorage.removeItem("clientesVinculados");
    localStorage.removeItem("pinia-mitienda");

    router.push("/login");
    isRedirecting = false;
  });
}
